"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";
import { resolveToolkitIcon } from "@/metadata/toolkit-icons";

// The fallback avatar shares the branded avatar's neutral `bg-muted/40` surface
// and carries identity in the letter color alone — so an unknown toolkit reads
// at the same emphasis as a branded one, not louder. A colored tint + same-hue
// ring (the previous approach) made fallbacks shout over real brand marks.
// Stable per name via a deterministic hash. Mid-lightness brand hues are used
// so the letter keeps usable contrast on the neutral surface in both light and
// dark modes (the brand tokens are single values, not light/dark ramps).
const LETTER_PALETTE = [
  "text-brand-text",
  "text-brand-fuchsia",
  "text-brand-red",
  "text-brand-purple",
  "text-brand-navy",
  "text-brand-green",
] as const;

function letterColorFor(name: string): string {
  let hash = 0;
  for (const ch of name) {
    hash = (hash * 31 + ch.charCodeAt(0)) % 1_000_003;
  }
  return LETTER_PALETTE[hash % LETTER_PALETTE.length] ?? LETTER_PALETTE[0];
}

const avatarVariants = cva(
  "inline-flex shrink-0 items-center justify-center font-semibold uppercase",
  {
    variants: {
      size: {
        xs: "size-4 rounded-sm text-[10px]",
        sm: "size-5 rounded-sm text-[10px]",
        md: "size-7 rounded-md text-xs",
        lg: "size-9 rounded-md text-sm",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

const iconSizeClass = {
  xs: "size-3",
  sm: "size-3.5",
  md: "size-4",
  lg: "size-5",
} as const;

export type McpToolkitAvatarProps = {
  /** Toolkit display name — used for the letter fallback. */
  name: string;
  /** Toolkit ID — used to look up the brand icon. Omit to force fallback. */
  toolkitId?: string;
} & VariantProps<typeof avatarVariants> &
  Omit<React.ComponentProps<"span">, "children">;

function McpToolkitAvatarImpl({
  name,
  toolkitId,
  size = "md",
  className,
  ...rest
}: McpToolkitAvatarProps) {
  const { Icon, hasBrandIcon } = resolveToolkitIcon(toolkitId);
  const safeSize = size ?? "md";

  if (hasBrandIcon) {
    return (
      <span
        {...rest}
        aria-hidden
        className={cn(
          avatarVariants({ size: safeSize }),
          "bg-muted/40",
          className
        )}
        data-slot="mcp-toolkit-avatar"
        data-variant="brand"
      >
        <Icon className={iconSizeClass[safeSize]} />
      </span>
    );
  }

  return (
    <span
      {...rest}
      aria-hidden
      className={cn(
        avatarVariants({ size: safeSize }),
        "bg-muted/40",
        letterColorFor(name),
        className
      )}
      data-slot="mcp-toolkit-avatar"
      data-variant="fallback"
    >
      {name.charAt(0)}
    </span>
  );
}

export const McpToolkitAvatar = React.memo(McpToolkitAvatarImpl);
export { avatarVariants as mcpToolkitAvatarVariants };
